import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fill-height map-container" }
const _hoisted_2 = { class: "d-inline-flex flex-shrink-0" }

import {
  ref,
  onMounted,
  computed,
  watch,
  ComputedRef,
  inject,
} from "vue";
import type { Partner } from "@emblem/api";
import maplibregl, { Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import mapStyle from "../maps/style";

import { colors } from "../helpers/category-colors";

import PartnerCard from "./PartnerCard.vue";

import { Geolocation } from "@capacitor/geolocation";
import type { Position as GeolocationPosition } from "@capacitor/geolocation";

type Place = Partner["places"][number];
type Location = Place["location"];

type MapPlace = ReturnType<typeof placesFromPartners>[number];


export default /*#__PURE__*/_defineComponent({
  __name: 'OffersMap',
  props: {
    partners: null
  },
  setup(__props: any) {

const props = __props as { partners: Partner[] }

const MIN_ZOOM = 12;

function haversineDistance(pointA: Location, pointB: Location) {
  const radius = 6371; // km

  //convert latitude and longitude to radians
  const deltaLatitude = ((pointB.latitude - pointA.latitude) * Math.PI) / 180;
  const deltaLongitude =
    ((pointB.longitude - pointA.longitude) * Math.PI) / 180;

  const halfChordLength =
    Math.cos((pointA.latitude * Math.PI) / 180) *
      Math.cos((pointB.latitude * Math.PI) / 180) *
      Math.sin(deltaLongitude / 2) *
      Math.sin(deltaLongitude / 2) +
    Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2);

  const angularDistance =
    2 * Math.atan2(Math.sqrt(halfChordLength), Math.sqrt(1 - halfChordLength));

  return radius * angularDistance;
}

const grenoble = {
  lat: 45.1885,
  lng: 5.7245,
};



const mapContainer = ref<HTMLElement>();
const placesContainer = ref<HTMLElement>();

let map: maplibregl.Map | undefined;

const usersLocation = ref({
  latitude: grenoble.lat,
  longitude: grenoble.lng,
});

function placesFromPartners(partners: Partner[]) {
  const places = partners
    .map((partner) =>
      partner.places.map((place) => ({
        partner,
        place,
        distance: haversineDistance(place.location, usersLocation.value),
        id: partner._id + JSON.stringify(place.location),
      }))
    )
    .flat();
  places.sort((a, b) => a.distance - b.distance);
  return places;
}

const partnerList = inject("partners:list") as ComputedRef<Partner[]>;
const allPlaces = placesFromPartners(partnerList.value);

const places = computed(() => {
  return placesFromPartners(props.partners);
});

const currentPlace = ref<Place>();
let __map_setCurrentPlace: (place?: Place, prevPlace?: Place) => void;
function onCardClick(place: Place) {
  currentPlace.value = place;
  if (__map_setCurrentPlace) {
    __map_setCurrentPlace(place, currentPlace.value);
  }
}

currentPlace.value = places.value[0]?.place;

const markers = new Map<Place, Marker>();

function setMarkersVisibility(places: MapPlace[]) {
  const placesSet = new Set(places.map((p) => p.place));
  // only show places in the array on the map
  markers.forEach((m, place) => {
    if (placesSet.has(place)) m._element.classList.remove("hidden-place");
    else m._element.classList.add("hidden-place");
  });
}

watch(places, (places) => {
  setMarkersVisibility(places);
});

function getCardSyle(partner: Partner) {
  const style: Record<string, string> = {};
  const category = partner.categories[0];
  if (category) {
    style["border-color"] = colors[category];
  }
  return style;
}

onMounted(async () => {
  const _map = new maplibregl.Map({
    container: mapContainer.value as HTMLElement,
    style: mapStyle,
    center: [usersLocation.value.longitude, usersLocation.value.latitude],
    zoom: 14, // starting zoom
  });
  map = _map;

  map.on("load", () => {
    allPlaces.forEach((p) => {
      const marker = new Marker();
      marker.setLngLat([p.place.location.longitude, p.place.location.latitude]);
      marker.addTo(_map);
      const el = marker._element;
      markers.set(p.place, marker);
      el.addEventListener("click", () => {
        currentPlace.value = p.place;
      });
      const category = p.partner.categories[0];
      if (category) {
        el.style.color = colors[category];
      }
    });
    setMarkersVisibility(places.value);

    const geoLocateControl = new maplibregl.GeolocateControl({
      geolocation: Geolocation,
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    } as any);
    geoLocateControl.on("geolocate", (position: GeolocationPosition) => {
      usersLocation.value = position.coords;
    });
    _map.addControl(geoLocateControl, "bottom-right");

    const setCurrentPlace = (place?: Place, prevPlace?: Place) => {
      if (place) {
        const { latitude, longitude } = place.location;
        if (prevPlace) {
          markers
            .get(prevPlace)
            ?._element.classList.remove("current-place-marker");
        }
        markers.get(place)?._element.classList.add("current-place-marker");

        _map.flyTo({
          center: [longitude, latitude],
          zoom: Math.max(_map.getZoom(), MIN_ZOOM),
        });
        if (placesContainer.value && placesContainer.value.firstElementChild) {
          const container = placesContainer.value.firstElementChild;
          const children = Array.from(container.children).filter((el) =>
            el.classList.contains("v-card")
          );
          const i = places.value.findIndex((p) => p.place === place);
          const card = children[i];
          card.scrollIntoView({ behavior: "smooth", inline: "center" });
        }
      }
    };
    __map_setCurrentPlace = setCurrentPlace;

    watch(currentPlace, setCurrentPlace);
    // avoid: "Geolocate control triggered before added to a map" warning
    setTimeout(() => {
      geoLocateControl.trigger();
    }, 100);
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "map",
      ref_key: "mapContainer",
      ref: mapContainer
    }, null, 512),
    _createElementVNode("div", {
      class: "map-places pa-2",
      ref_key: "placesContainer",
      ref: placesContainer
    }, [
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(places), ({ partner, id, place }) => {
          return (_openBlock(), _createBlock(PartnerCard, {
            class: _normalizeClass({
            'flex-shrink-0': true,
            'mr-2': true,
            'current-place': currentPlace.value === place,
          }),
            style: _normalizeStyle(getCardSyle(partner)),
            key: id,
            partner: partner,
            "min-width": "50vw",
            "offers-in-dialog": "",
            onClick: ($event: any) => (onCardClick(place))
          }, null, 8, ["class", "style", "partner", "onClick"]))
        }), 128))
      ])
    ], 512)
  ]))
}
}

})